// Putt-replay math for the 3D animation. Unlike the web prototype (which
// *simulated* a putt from speed+stimp), the app *replays a measured putt*: we
// have speedMps, distanceFt (the actual stop), angleDeg and proximityFt from the
// PuttRecord, so we animate the ball to its known resting place with realistic
// deceleration. Make/miss comes from the app's own verdict, not a re-simulation.

import { GIMME_FT } from './courses';

export const BALL_RADIUS_FT = 0.07;
export const CUP_RADIUS_FT = 0.177;
/** The Sim 9 gimme circle's radius (ft): the default ring PuttScene draws
 *  when a Sim 9 screen asks for it (`showGimme`). It IS the Sim 9 rule, so it
 *  is the rule's own constant (courses.ts GIMME_FT, 5 ft since 2026-10-19).
 *  No other mode uses it: Baseball, Football, HORSE, Poker and the rest pass
 *  their own radii and keep their own numbers. */
export const SIM9_GIMME_RADIUS_FT = GIMME_FT;
/** The dormant Contour mode's 3 ft gimme circle (Contour3DStage only). Not
 *  the Sim 9 gimme: it stays 3 ft. */
export const CONTOUR_GIMME_RADIUS_FT = 3;
export const FLAGSTICK_HEIGHT_FT = 7.0;
export const M_PER_FT = 0.3048;
export const FT_PER_M = 1 / M_PER_FT;

// Review timeline (seconds) — roll, settle, zoom out, hold, zoom back.
// Post-putt review keeps the SAME pitch/yaw as the stopped chase camera. It
// widens the lens and, only when necessary, dollies straight backward. The old
// move to a steep overhead view made repeated putts disorienting.
//
// TIGHTENED 2026-07-27 — the total was 7.4 s of dead time after every roll,
// which is a long wait between rungs of a ladder (Cole: "the post putt takes
// too long"). The settled review hold was then extended by 0.5 s on 2026-08-06
// so the result has time to register before the camera returns. These are
// SHARED by every mode that mounts PuttScene (Target, Challenge, Sim 9), not
// ladder-only.
export const FLYOVER_DELAY_S = 0.6;
export const FLYOVER_S = 1.1;
export const REVIEW_HOLD_S = 2.1;
export const RETURN_S = 0.9;

export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
export const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

/** Constant deceleration (ft/s^2) that brings a ball entering at `speedFtS` to
 *  rest after exactly `distanceFt`. Falls back to a stimp-style decel if the
 *  measured pair is degenerate. */
export function decelFor(speedFtS: number, distanceFt: number, stimp = 10): number {
  if (speedFtS > 0.05 && distanceFt > 0.05) return (speedFtS * speedFtS) / (2 * distanceFt);
  return (6 * 6) / (2 * stimp); // stimpmeter fallback
}

/** Ball roll distance (ft) at time t given entry speed + deceleration. */
export function puttPosition(t: number, speedFtS: number, a: number): number {
  const stopTime = a > 0 ? speedFtS / a : 0;
  const c = Math.min(Math.max(t, 0), stopTime);
  return speedFtS * c - 0.5 * a * c * c;
}

/** Lateral offset (ft) at longitudinal distance d for a start-line angle. */
export const lateralAt = (d: number, deg: number) => d * Math.tan((deg * Math.PI) / 180);

/**
 * Lateral resting coordinate for a target-based replay.
 *
 * Target scoring owns the authoritative radial proximity. Reconstructing its
 * lateral component from that proximity keeps a rendered ball on the same
 * side of a scoring-ring boundary as the verdict, even though the measured
 * start-line angle begins at the gate rather than at the address ball.
 * Without an authoritative proximity (or for a make that will sink), retain
 * the historical straight angle projection.
 */
export function targetReplayRestLateralFt(args: {
  distanceFt: number;
  targetFt: number;
  angleDeg: number;
  proximityFt: number | null;
  made: boolean;
}): number {
  const { distanceFt, targetFt, angleDeg, proximityFt, made } = args;
  if (made || proximityFt == null || !Number.isFinite(proximityFt)) {
    return lateralAt(distanceFt, angleDeg);
  }
  const longitudinalFt = distanceFt - targetFt;
  const lateralMagnitudeFt = Math.sqrt(
    Math.max(0, proximityFt * proximityFt - longitudinalFt * longitudinalFt),
  );
  // A LONGITUDINAL-ONLY proximity (the firmware fallback some records carry)
  // cancels to ~0 here even though the measured line clearly bends — the
  // Studio dead-straight bug, seen again in streak mode 2026-08-18. A radial
  // proximity can never reconstruct near-zero while the angle projection is
  // clearly bent (the judge would have folded that lateral in), so treat that
  // combination as non-radial input and keep the projection.
  const projectedFt = Math.abs(lateralAt(distanceFt, angleDeg));
  if (lateralMagnitudeFt < 0.02 && projectedFt > 0.05) {
    return lateralAt(distanceFt, angleDeg);
  }
  return (angleDeg < 0 ? -1 : 1) * lateralMagnitudeFt;
}

export interface Phase {
  /** 0 = play view, 1 = full review zoom/hold, back to 0 on return. */
  flyT: number;
  /** true once the ball has come to rest. */
  settled: boolean;
  /** true while the review camera is engaged. */
  reviewing: boolean;
  /** true once the whole timeline (incl. return) has finished. */
  done: boolean;
}

/** Beat held on the settled ball when the review zoom is suppressed —
 *  long enough to register the result, short enough to keep a streak's
 *  putt-putt-putt rhythm. */
export const NO_REVIEW_BEAT_S = 0.8;

/** Timeline with the review zoom suppressed: roll, settle, a short beat
 *  at rest, done. Hosts that skip the recap use this — Start Line streak
 *  (every miss is inches away; the review showed nothing) and Sim 9 makes
 *  (the ball is IN — there is no leave to review). */
/**
 * The review timeline with no return leg: up to the review frame, hold, then
 * done. The camera stays on the review frame (SceneContent's holdReviewFrame)
 * instead of flying back to the address, for a putt whose round is over and
 * whose result goes up as an overlay (chase mode out of reach, Cole,
 * 2026-10-13: "we shouldn't have to zoom all the way back").
 */
export function timelineHoldReviewAt(elapsedS: number, stopTimeS: number): Phase {
  const upStart = stopTimeS + FLYOVER_DELAY_S;
  const upEnd = upStart + FLYOVER_S;
  const holdEnd = upEnd + REVIEW_HOLD_S;
  let flyT = 0;
  if (elapsedS >= upEnd) flyT = 1;
  else if (elapsedS >= upStart) flyT = (elapsedS - upStart) / FLYOVER_S;
  flyT = clamp(flyT, 0, 1);
  return { flyT, settled: elapsedS >= stopTimeS, reviewing: flyT > 0, done: elapsedS >= holdEnd };
}

export function timelineNoReviewAt(elapsedS: number, stopTimeS: number): Phase {
  return {
    flyT: 0,
    settled: elapsedS >= stopTimeS,
    reviewing: false,
    done: elapsedS >= stopTimeS + NO_REVIEW_BEAT_S,
  };
}

/** Wall-clock length (ms) of a replay when the review zoom is suppressed
 *  (see timelineNoReviewAt) — roll + rest beat + a small pad. */
export function noReviewReplayMs(
  putt: { speedMps?: number; distanceFt: number },
  stimp: number
): number {
  const speedFtS =
    putt.speedMps && putt.speedMps > 0.05
      ? putt.speedMps * FT_PER_M
      : Math.sqrt((36 / Math.max(stimp, 1)) * Math.max(putt.distanceFt, 0.1));
  const stopS =
    speedFtS > 0.05 && putt.distanceFt > 0.05 ? (2 * putt.distanceFt) / speedFtS : 0;
  return Math.min(Math.round((stopS + NO_REVIEW_BEAT_S) * 1000) + 350, 30_000);
}

/** Resolve the review-zoom phase from elapsed time since replay start. */
export function timelineAt(elapsedS: number, stopTimeS: number): Phase {
  const upStart = stopTimeS + FLYOVER_DELAY_S;
  const upEnd = upStart + FLYOVER_S;
  const holdEnd = upEnd + REVIEW_HOLD_S;
  const downEnd = holdEnd + RETURN_S;
  let flyT = 0;
  let done = false;
  if (elapsedS >= downEnd) {
    flyT = 0;
    done = true;
  } else if (elapsedS >= holdEnd) {
    flyT = 1 - (elapsedS - holdEnd) / RETURN_S; // fly back 1 -> 0
  } else if (elapsedS >= upEnd) {
    flyT = 1; // hold review
  } else if (elapsedS >= upStart) {
    flyT = (elapsedS - upStart) / FLYOVER_S; // fly up 0 -> 1
  }
  flyT = clamp(flyT, 0, 1);
  return { flyT, settled: elapsedS >= stopTimeS, reviewing: flyT > 0, done };
}

export interface CamFrame {
  pos: [number, number, number];
  look: [number, number, number];
  fov: number;
}

const REVIEW_FOV = 55;

/** Play-view FOV: wider + up-tilted for close pins so the 7 ft flag clears
 *  the frame top. Shared by cameraFor()'s scripted framing and the chase cam,
 *  so the mid-sequence handoff between them never breathes the lens. (It was
 *  originally shared to keep the FOLLOW/WIDE toggle from zooming; that toggle
 *  is gone, but the handoff it protected is still there.) */
export const playFovFor = (targetFt: number) => clamp(54 - (targetFt - 3) * 0.8, 40, 54);

/** Address-camera placement for a given timeline phase. Position and look
 *  direction never change here; only the lens eases wider for the return leg.
 *  The stopped-ball review itself is supplied by reviewCameraFor(). */
export function cameraFor(
  targetFt: number,
  flyT: number,
  _stopFt: number,
  _restX: number,
  /** Retained in the shared API for ladder callers. The fixed-perspective
   *  review no longer aims at a computed bounding box. */
  _keepInFrameFt?: number | null
): CamFrame {
  const t = targetFt;
  const playFov = playFovFor(t);
  const lookZ = clamp(t * 0.85, 5, 24);
  const lookY = clamp(3.4 - t * 0.08, 2.0, 3.4);
  const playPos: [number, number, number] = [0, 2.5, 7];
  const playLook: [number, number, number] = [0, lookY, -lookZ];
  if (flyT <= 0) return { pos: playPos, look: playLook, fov: playFov };

  const e = easeInOut(flyT);
  return {
    pos: playPos,
    look: playLook,
    fov: lerp(playFov, REVIEW_FOV, e),
  };
}

/** Chase-cam frame: trails the rolling ball from ~7 ft behind and 2.5 ft up
 *  (the play camera's height), looking just ahead of it down-range so the
 *  ball rides in the lower third of the frame. ballX/ballZ are the ball's
 *  world position (z negative down-range). The camera PARKS 4 ft short of
 *  the pin — on a big overrun it stops at the hole and watches the ball
 *  roll away. (Trailing through the pin would sweep the lens through the
 *  flagstick, and a look point clamped near the cup flipped the view
 *  backwards once the ball out-ran it.) The look point always tracks ahead
 *  of the ball, so it can never end up behind the camera. */
export function chaseCameraFor(ballX: number, ballZ: number, targetFt: number): CamFrame {
  const camZ = Math.max(ballZ + 7, -(targetFt - 4));
  return {
    pos: [ballX * 0.6, 2.5, camZ],
    look: [ballX, 0.35, ballZ - 5],
    fov: playFovFor(targetFt),
  };
}

/** Minimum backward travel along the existing view axis needed to fit every
 *  point. Defaults to a conservative 4:3 viewport for non-render callers. */
function dollyBackDistanceToContain(
  frame: CamFrame,
  points: Array<[number, number, number]>,
  aspect = 4 / 3,
  /** Fraction of the screen height at the bottom that a HUD covers (0-0.4). */
  bottomInset = 0
): number {
  const [px, py, pz] = frame.pos;
  const fx0 = frame.look[0] - px;
  const fy0 = frame.look[1] - py;
  const fz0 = frame.look[2] - pz;
  const fLen = Math.hypot(fx0, fy0, fz0) || 1;
  const fx = fx0 / fLen;
  const fy = fy0 / fLen;
  const fz = fz0 / fLen;

  // Camera right = forward × world-up; camera up = right × forward.
  const horizontalLen = Math.hypot(fx, fz) || 1;
  const rx = -fz / horizontalLen;
  const rz = fx / horizontalLen;
  const ux = -rz * fy;
  const uy = rz * fx - rx * fz;
  const uz = rx * fy;
  const margin = 0.9;
  const tanHalfFov = Math.tan((frame.fov * Math.PI) / 360);
  let dollyFt = 0;

  for (const [x, y, z] of points) {
    const dx = x - px;
    const dy = y - py;
    const dz = z - pz;
    const depth = dx * fx + dy * fy + dz * fz;
    const cameraX = dx * rx + dz * rz;
    const cameraY = dx * ux + dy * uy + dz * uz;
    // Below center, the usable half-height stops where the HUD begins.
    const half = cameraY < 0 ? 1 - 2 * Math.min(0.4, Math.max(0, bottomInset)) : 1;
    const requiredDepth = Math.max(
      Math.abs(cameraY) / (tanHalfFov * margin * half),
      Math.abs(cameraX) / (tanHalfFov * aspect * margin)
    );
    dollyFt = Math.max(dollyFt, requiredDepth - depth);
  }

  return Math.max(0, dollyFt);
}

/** Move a camera backward along its existing axis by the minimum distance
 * required to contain every point. */
function dollyBackToContain(
  frame: CamFrame,
  points: Array<[number, number, number]>,
  aspect = 4 / 3,
  bottomInset = 0
): CamFrame {
  const dollyFt = dollyBackDistanceToContain(frame, points, aspect, bottomInset);
  if (dollyFt <= 0) return frame;

  const [px, py, pz] = frame.pos;
  const fx0 = frame.look[0] - px;
  const fy0 = frame.look[1] - py;
  const fz0 = frame.look[2] - pz;
  const fLen = Math.hypot(fx0, fy0, fz0) || 1;
  const fx = fx0 / fLen;
  const fy = fy0 / fLen;
  const fz = fz0 / fLen;
  return {
    pos: [px - fx * dollyFt, py - fy * dollyFt, pz - fz * dollyFt],
    look: [
      frame.look[0] - fx * dollyFt,
      frame.look[1] - fy * dollyFt,
      frame.look[2] - fz * dollyFt,
    ],
    fov: frame.fov,
  };
}

/** Post-putt review frame. Keep the exact stopped chase frame when the ball and
 *  hole already share the viewport. Otherwise widen the lens and dolly
 *  straight backward only as far as needed. The view direction never rotates;
 *  the separate return phase eases back to address afterward. */
export function reviewCameraFor(
  restX: number,
  stopFt: number,
  targetFt: number,
  keepInFrameFt?: number | null,
  aspect = 4 / 3,
  /** TV hosts: keep the scoring rings in view and everything clear of the
   *  putt strip along the bottom (Cole, 2026-10-11: the Poker review ended so
   *  close that the yellow ring ran off the screen and the strip sat on the
   *  cup). The phone passes nothing and frames exactly as before. */
  keep?: { ringRadiusFt?: number | null; bottomInset?: number }
): CamFrame {
  const chaseAtRest = chaseCameraFor(restX, -stopFt, targetFt);
  const ringFt = keep?.ringRadiusFt != null && keep.ringRadiusFt > 0 ? keep.ringRadiusFt : 0;
  const bottomInset = keep?.bottomInset ?? 0;
  const ringPoints: Array<[number, number, number]> = ringFt > 0
    ? [[-ringFt, 0, -targetFt], [ringFt, 0, -targetFt], [0, 0, -targetFt + ringFt], [0, 0, -targetFt - ringFt]]
    : [];
  // Decide whether a review move is necessary from what the golfer actually
  // needs to compare: the resting ball and the hole. Ladder has no hole, so
  // its equivalent is the ball plus the target-line span. Do this BEFORE
  // widening the lens; widening first made every putt zoom even when both
  // objects were already on the same screen.
  const visibilityPoints: Array<[number, number, number]> = keepInFrameFt != null
    ? [
        [restX, BALL_RADIUS_FT * 2, -stopFt],
        [-5, 0.8, -keepInFrameFt],
        [5, 0.8, -keepInFrameFt],
      ]
    : [
        [restX, BALL_RADIUS_FT * 2, -stopFt],
        [0, 0, -targetFt],
        ...ringPoints,
      ];

  if (dollyBackDistanceToContain(chaseAtRest, visibilityPoints, aspect, bottomInset) <= 0) {
    return chaseAtRest;
  }

  const importantPoints: Array<[number, number, number]> = keepInFrameFt != null
    ? [
        [restX, BALL_RADIUS_FT * 2, -stopFt],
        [-5, 0.8, -keepInFrameFt],
        [5, 0.8, -keepInFrameFt],
      ]
    : [
        [restX, BALL_RADIUS_FT * 2, -stopFt],
        [0, 0, -targetFt],
        [0, FLAGSTICK_HEIGHT_FT, -targetFt],
        ...ringPoints,
      ];
  return dollyBackToContain(
    {
      ...chaseAtRest,
      fov: Math.max(chaseAtRest.fov, REVIEW_FOV),
    },
    importantPoints,
    aspect,
    bottomInset
  );
}

/** Wall-clock length (ms) of a putt's full 3D replay — roll + flyover +
 *  review hold (+ a small pad; the fly-back is deliberately excluded).
 *  Round screens use this to defer their summary flip until the LAST
 *  putt's replay has played out instead of cutting it off. Mirrors
 *  PuttScene's speed fallback exactly: measured entry speed when present,
 *  else the stimp-derived speed that stops at the measured distance. */
export function fullReplayMs(
  putt: { speedMps?: number; distanceFt: number },
  stimp: number
): number {
  const speedFtS =
    putt.speedMps && putt.speedMps > 0.05
      ? putt.speedMps * FT_PER_M
      : Math.sqrt((36 / Math.max(stimp, 1)) * Math.max(putt.distanceFt, 0.1));
  const stopS =
    speedFtS > 0.05 && putt.distanceFt > 0.05 ? (2 * putt.distanceFt) / speedFtS : 0;
  // Cap the wait: a corrupt-but-validation-passing packet (tiny speed, big
  // distance) must never arm a minutes-long summary timer.
  return Math.min(
    Math.round((stopS + FLYOVER_DELAY_S + FLYOVER_S + REVIEW_HOLD_S) * 1000) + 350,
    30_000
  );
}

/** Component-wise lerp between two camera frames. */
export function lerpFrame(a: CamFrame, b: CamFrame, t: number): CamFrame {
  return {
    pos: [lerp(a.pos[0], b.pos[0], t), lerp(a.pos[1], b.pos[1], t), lerp(a.pos[2], b.pos[2], t)],
    look: [
      lerp(a.look[0], b.look[0], t),
      lerp(a.look[1], b.look[1], t),
      lerp(a.look[2], b.look[2], t),
    ],
    fov: lerp(a.fov, b.fov, t),
  };
}

/** Dynamic fog far-plane: push it back during review so a far ball/cup never
 *  fades into haze. Base sits beyond the horizon ring (230 ft) and most of
 *  the ground plane so the backdrop always renders — fog is a gentle
 *  horizon blend, not a curtain. */
export function fogFarAt(flyT: number, stopFt: number, targetFt: number): number {
  const base = 650;
  const far = flyT > 0 ? Math.max(base, Math.max(stopFt, targetFt) * 3 + 120) : base;
  return lerp(base, far, easeInOut(flyT));
}
